/* eslint-disable @typescript-eslint/no-explicit-any */
import { Phone, MessageCircleMore, SquarePen, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import BackNavigator from "./components/back-navigation";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useGroupProvider } from "@/context/group-provider";
import { Card } from "@/components/ui/card";
import { STORAGE, STORAGE_KEY } from "@/constants";
import MessageSupport from "./message-support";
import { useEffect, useRef, useState } from "react";
import { removeItem } from "@/lib/storage-utils";
import { getProductConfig } from "@/config/product-config";
import { useTheme } from "@/context/theme-provider";
import { CLIENT_NAME } from "@/lib/client-utils";
import IDENTIFIERS from "@/constants/test_id";
import {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import { usePanelLogger } from "@/services/logger/logger-hooks";
import { PanelTracker } from "@/services/logger/smart-context-provider";
import ScreenRouteNames from "@/constants/screen-route-names";
import { useFocusOnOpen } from "@/hooks/use-focus-on-open";

const GroupInfo = ({ onClose }: { onClose: () => void }) => {
  const [isMessageFormOpen, setIsMessageFormOpen] = useState<boolean>(false);
  const { groupData, setGroupData } = useGroupProvider();
  const { changeTheme } = useTheme();
  const navigate = useNavigate();
  const { logPanelOpen, logPanelClose } = usePanelLogger();
  /**
   * The "Message Support" sheet slides up over this content rather than replacing it
   * (nothing here unmounts), so the trigger button stays a stable node the whole time —
   * unlike the group.tsx/login.tsx panel-swap pattern, no data-testid re-query is needed
   * to restore focus to it on close.
   */
  const messageFormRef = useRef<HTMLDivElement>(null);
  const messageSupportTriggerRef = useRef<HTMLButtonElement>(null);
  const restoreFocusOnCloseRef = useRef(false);

  const closeMessageForm = () => {
    setIsMessageFormOpen(false);
    logPanelClose(
      "message_support",
      { panelName: "Message Support" },
      {
        category: LogCategories.MESSAGE_SUPPORT,
        subCategory: LogSubCategories.GROUP_INFO_MODAL,
        type: "PAGE_VIEW",
      },
    );
  };

  // 2.4.3 Focus Order — A: moves focus into the sheet when it opens, and wires Escape
  // as a shortcut back. Not a focus trap — the content underneath is visually covered
  // but nothing here is a modal blocking the rest of the page.
  useFocusOnOpen(messageFormRef, isMessageFormOpen, closeMessageForm);

  useEffect(() => {
    if (isMessageFormOpen || !restoreFocusOnCloseRef.current) return;
    restoreFocusOnCloseRef.current = false;
    messageSupportTriggerRef.current?.focus();
  }, [isMessageFormOpen]);

  return (
    // 1.3.1 Info and Relationships — A: this is rendered inline inside the page's own
    // <main> (login.tsx), never on its own — a nested <main> is an invalid, duplicate
    // landmark. It's a content section of that page, not a page of its own.
    <div className="flex h-full flex-col items-center justify-center space-y-4 px-10">
      <BackNavigator
        title="Group Information"
        right={
          <Button
            variant="ghost"
            aria-label="Close"
            className="h-auto w-fit p-0 hover:bg-transparent"
            onClick={onClose}
            data-testid={IDENTIFIERS.GROUP_INFO.CLOSE_BUTTON}
          >
            <X className="cursor-pointer" size={20} />
          </Button>
        }
      />
      <div className="place-items-center space-y-4">
        <div className="place-items-center">
          <img
            src={
              groupData?.client_logo
                ? `data:image/png;base64,${groupData.client_logo}`
                : undefined
            }
            alt="Login"
            className="mb-6 w-40 object-cover"
          />
          <Label className="mb-6 flex items-center justify-center text-sm">
            Your Group ID: {groupData?.entered_id}
            <Button
              variant="link"
              aria-label="Edit Group Id"
              className="ml-2 h-4 p-0"
              onClick={() => {
                setGroupData(null);
                removeItem(STORAGE_KEY.PRODUCT_CONFIG, STORAGE.SESSION);
                removeItem(STORAGE_KEY.CACHED_GROUP_DETAIL, STORAGE.LOCAL);
                const productConfig = getProductConfig();
                changeTheme(productConfig.theme);
                (window as any).__PRODUCT_CONFIG__ = productConfig;
                navigate(ScreenRouteNames.ROOT.path, { replace: true });
              }}
              data-testid={IDENTIFIERS.GROUP_INFO.CHANGE_GROUP_BUTTON}
            >
              <SquarePen size={16} className="cursor-pointer" />
            </Button>
          </Label>
          <Label className="rounded-sm bg-muted p-2 text-sm">
            Your Customer Support is {groupData?.service_provider}
          </Label>
        </div>

        <Card className="flex w-full items-center justify-center gap-2 px-4 py-2">
          <img
            src={
              groupData?.group_logo
                ? `data:image/png;base64,${groupData.group_logo}`
                : undefined
            }
            alt="Login"
            className="my-2 aspect-square w-16 object-contain"
          />
          <div className="flex flex-col items-start justify-center gap-1">
            <span className="text-base/8 font-bold">
              {groupData?.service_provider}
            </span>
            {Boolean(groupData?.display_email) && (
              <Label className="text-center text-sm font-bold">
                Email:{" "}
                <Button
                  href={`mailto:${groupData?.service_provider_email}`}
                  target="_blank"
                  className="relative top-[5px] inline-block w-[170px] truncate text-left text-sm font-normal text-primary no-underline hover:underline"
                  data-testid={
                    IDENTIFIERS.GROUP_INFO.SERVICE_PROVIDER_EMAIL_LINK
                  }
                >
                  {groupData?.display_email}
                </Button>
              </Label>
            )}
            {Boolean(groupData?.display_website) && (
              <Label className="text-center text-sm font-bold">
                Website:{" "}
                <Button
                  href={groupData?.website}
                  target="_blank"
                  className="relative top-[5px] inline-block w-[170px] truncate text-left text-sm font-normal text-primary no-underline hover:underline"
                  data-testid={
                    IDENTIFIERS.GROUP_INFO.SERVICE_PROVIDER_SUPPORT_NUMBER
                  }
                >
                  {groupData?.display_website}
                </Button>
              </Label>
            )}
            <Label className="text-center text-sm font-bold">
              Phone:{" "}
              <Label
                className="text-sm font-normal"
                data-testid={
                  IDENTIFIERS.GROUP_INFO.SERVICE_PROVIDER_SUPPORT_NUMBER
                }
              >
                {groupData?.phone || groupData?.support_number}
              </Label>
            </Label>
          </div>
        </Card>

        <div className="flex w-full flex-col justify-center gap-4 sm:flex-row">
          <div className="w-full lg:w-1/2">
            <Button
              size="lg"
              href={`tel:${groupData?.support_number}`}
              linkStyle="button"
              // 4.1.2 Name, Role, Value — A: `href` always renders a native <a>, so
              // this is announced as "link" by default no matter what linkStyle
              // looks like — linkStyle only changes CSS classes, not semantics.
              // It's styled and behaves as a button (triggers a call), so its role
              // is corrected to match.
              role="button"
              variant="default"
              aria-label="Call Now"
              className="flex w-full gap-2 px-4 text-sm no-underline lg:px-6"
              data-testid={IDENTIFIERS.GROUP_INFO.CALL_NOW_BUTTON}
            >
              <Phone className="" /> Call Now
            </Button>
          </div>

          {!groupData?.group_id?.startsWith(CLIENT_NAME.COBALT) && (
            <div className="w-full lg:w-1/2">
              <Button
                ref={messageSupportTriggerRef}
                size="lg"
                onClick={() => {
                  restoreFocusOnCloseRef.current = true;
                  setIsMessageFormOpen(true);
                  logPanelOpen(
                    "message_support",
                    { panelName: "Message Support" },
                    {
                      category: LogCategories.MESSAGE_SUPPORT,
                      subCategory: LogSubCategories.GROUP_INFO_MODAL,
                      type: "PAGE_VIEW",
                    },
                  );
                }}
                variant="default"
                aria-label="Message Support"
                className="flex w-full gap-2 px-4 text-sm lg:px-6"
                data-testid={IDENTIFIERS.GROUP_INFO.MESSAGE_SUPPORT_BUTTON}
              >
                <MessageCircleMore className="" />
                <span>Message Support</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      <div
        ref={messageFormRef}
        className={`absolute inset-x-0 bottom-0 mx-1 flex h-full flex-col overflow-hidden rounded-t-xl bg-white transition-transform duration-300 ease-in-out ${isMessageFormOpen ? "block translate-y-0" : "hidden translate-y-full"}`}
      >
        {isMessageFormOpen && (
          <PanelTracker
            panelName="Message Support"
            tag={LogTags.SUPPORT}
            category={LogCategories.MESSAGE_SUPPORT}
            subCategory={LogSubCategories.GROUP_INFO_MODAL}
          >
            <MessageSupport
              isOpen={isMessageFormOpen}
              setIsOpen={setIsMessageFormOpen}
            />
          </PanelTracker>
        )}
      </div>
    </div>
  );
};

export default GroupInfo;
